// TherapistsDirectory.jsx - Browse All Massage Therapists
"use client";
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Search,
  MapPin,
  Star,
  Filter,
  ArrowRight,
  User,
  Loader2,
} from "lucide-react";
import { NavbarDemo } from "./NavBar";
import SEOHead from "./SEOHead";
import ChatBot from "./ChatBot";
import { API_ENDPOINTS, DEFAULT_FETCH_CONFIG } from "../config/api.config";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export default function TherapistsDirectory() {
  const navigate = useNavigate();
  const [therapists, setTherapists] = useState([]);
  const [filteredTherapists, setFilteredTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCity, setSelectedCity] = useState("all");
  const [selectedSpecialty, setSelectedSpecialty] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  // Extract unique cities and specialties for filters
  const cities = [
    "all",
    ...new Set(
      therapists
        .map((t) => t.city || t.clinicLocation?.city)
        .filter(Boolean)
    ),
  ];

  const specialties = [
    "all",
    ...new Set(
      therapists.flatMap((t) => t.specialties || []).filter(Boolean)
    ),
  ];

  useEffect(() => {
    fetchAllTherapists();
  }, []);

  useEffect(() => {
    filterAndSortTherapists();
  }, [therapists, searchQuery, selectedCity, selectedSpecialty, sortBy]);

  const fetchAllTherapists = async () => {
    try {
      setLoading(true);
      // Fetch all active therapists with slugs
      const response = await fetch(
        `${API_ENDPOINTS.therapists.search}?status=active&hasSlug=true`,
        DEFAULT_FETCH_CONFIG
      );

      if (!response.ok) {
        console.error("Failed to fetch therapists");
        setTherapists([]);
        return;
      }

      const data = await response.json();
      setTherapists(data.therapists || data || []);
    } catch (error) {
      console.error("Error fetching therapists:", error);
      setTherapists([]);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortTherapists = () => {
    let filtered = [...therapists];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.fullName?.toLowerCase().includes(query) ||
          t.bio?.toLowerCase().includes(query) ||
          t.bioOneLiner?.toLowerCase().includes(query) ||
          t.city?.toLowerCase().includes(query) ||
          t.specialties?.some((s) => s.toLowerCase().includes(query))
      );
    }

    // City filter
    if (selectedCity !== "all") {
      filtered = filtered.filter(
        (t) =>
          t.city === selectedCity ||
          t.clinicLocation?.city === selectedCity
      );
    }

    // Specialty filter
    if (selectedSpecialty !== "all") {
      filtered = filtered.filter((t) =>
        t.specialties?.includes(selectedSpecialty)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return (a.fullName || "").localeCompare(b.fullName || "");
        case "rating":
          return (b.averageRating || 0) - (a.averageRating || 0);
        case "reviews":
          return (b.totalReviews || 0) - (a.totalReviews || 0);
        default:
          return 0;
      }
    });

    setFilteredTherapists(filtered);
  };

  const seoTitle = "Find a Massage Therapist | Browse Licensed RMTs | BookaTherapy";
  const seoDescription = "Browse our directory of licensed massage therapists. Find the perfect RMT for your needs - search by location, specialty, and reviews. Book online instantly.";

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical="https://bookatherapy.com/find-therapist"
        keywords="find massage therapist, browse massage therapists, RMT directory, licensed massage therapist, book massage therapist online"
      />

      <div className="min-h-screen bg-gray-50">
        <NavbarDemo />
        <ChatBot />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#252F4F] via-[#1e2640] to-[#252F4F] py-10 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
                Find Your Perfect{" "}
                <span className="text-[#98C454]">Massage Therapist</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-6 sm:mb-8 max-w-2xl mx-auto">
                Browse our directory of licensed, professional massage therapists.
                Read reviews, view credentials, and book online.
              </p>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    type="text"
                    placeholder="Search by name, specialty, or location..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 pr-4 py-6 text-lg w-full rounded-full shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Filter className="h-4 w-4" />
                <span className="font-medium">
                  {filteredTherapists.length} therapist
                  {filteredTherapists.length !== 1 ? "s" : ""} found
                </span>
              </div>

              <div className="grid grid-cols-1 xs:grid-cols-2 sm:flex sm:flex-wrap gap-3 w-full sm:w-auto">
                {/* City Filter */}
                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="All Cities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cities</SelectItem>
                    {cities.slice(1).map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Specialty Filter */}
                <Select value={selectedSpecialty} onValueChange={setSelectedSpecialty}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="All Specialties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Specialties</SelectItem>
                    {specialties.slice(1).map((specialty) => (
                      <SelectItem key={specialty} value={specialty}>
                        {specialty}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Sort By */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="name">Name (A-Z)</SelectItem>
                    <SelectItem value="rating">Highest Rated</SelectItem>
                    <SelectItem value="reviews">Most Reviews</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>

        {/* Therapist Grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-[#98C454]" />
            </div>
          ) : filteredTherapists.length === 0 ? (
            <div className="text-center py-20">
              <User className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                No therapists found
              </h3>
              <p className="text-gray-600 mb-6">
                Try adjusting your search or filters
              </p>
              <Button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCity("all");
                  setSelectedSpecialty("all");
                }}
                className="bg-[#98C454] hover:bg-[#87b043]">
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTherapists.map((therapist) => (
                <TherapistCard key={therapist._id} therapist={therapist} />
              ))}
            </div>
          )}
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-[#252F4F] to-[#1e2640] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Book Your Massage?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Not sure which therapist to choose? Let us match you with the
              perfect therapist for your needs.
            </p>
            <Button
              size="lg"
              className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] font-bold text-lg px-10 py-6"
              onClick={() => navigate("/book")}>
              Start Booking Flow
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>
      </div>
    </>
  );
}

// Therapist Card Component
function TherapistCard({ therapist }) {
  const city = therapist.city || therapist.clinicLocation?.city;
  const postalCode = therapist.postalCode || therapist.clinicLocation?.postalCode;

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300 group">
      <Link to={`/massage-therapists/${therapist.slug}`}>
        {/* Photo */}
        <div className="relative h-48 bg-gray-100 overflow-hidden">
          {therapist.profilePhotoUrl ? (
            <img
              src={therapist.profilePhotoUrl}
              alt={therapist.fullName}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#98C454] to-[#87b043] flex items-center justify-center text-white text-4xl font-bold">
              {therapist.fullName?.charAt(0)}
            </div>
          )}
          {therapist.isAvailable && (
            <Badge className="absolute top-3 right-3 bg-[#98C454] text-white border-0">
              Available
            </Badge>
          )}
        </div>

        {/* Info */}
        <div className="p-5">
          <h3 className="text-xl font-bold text-[#252F4F] mb-2 group-hover:text-[#98C454] transition-colors">
            {therapist.fullName}
          </h3>

          {/* One-liner */}
          {therapist.bioOneLiner && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
              {therapist.bioOneLiner}
            </p>
          )}

          {/* Location */}
          {city && (
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <MapPin className="h-4 w-4 text-[#98C454]" />
              <span>
                {city}
                {postalCode && ` • ${postalCode}`}
              </span>
            </div>
          )}

          {/* Rating */}
          {therapist.averageRating > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(therapist.averageRating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {therapist.averageRating.toFixed(1)}
              </span>
              {therapist.totalReviews > 0 && (
                <span className="text-sm text-gray-500">
                  ({therapist.totalReviews})
                </span>
              )}
            </div>
          )}

          {/* Specialties */}
          {therapist.specialties && therapist.specialties.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {therapist.specialties.slice(0, 3).map((specialty) => (
                <Badge
                  key={specialty}
                  variant="outline"
                  className="text-xs border-[#98C454] text-gray-700">
                  {specialty}
                </Badge>
              ))}
              {therapist.specialties.length > 3 && (
                <Badge variant="outline" className="text-xs text-gray-500">
                  +{therapist.specialties.length - 3} more
                </Badge>
              )}
            </div>
          )}

          {/* View Profile Button */}
          <Button
            className="w-full bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] font-semibold"
            asChild>
            <Link to={`/massage-therapists/${therapist.slug}`}>
              View Profile
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </Link>
    </Card>
  );
}
